import { useAuth0 } from "@auth0/auth0-react";
import Result from "../../deal-builder/utils/result.utils.ts";
import PrimaryDatabaseAPI from "../../deal-builder/api/primary-db-api.ts";
import { useContext } from "react";
import { UserInfoContext } from "../../contexts/UserInfoContext.js";
import { DealStatus, DealBuilder } from "../../deal-builder/services/deal-builder.ts";
import useUsTreasury from "../../deal-builder/hooks/useUsTreasury.ts";
import useEODAAA from "../../deal-builder/hooks/useEODAAA.ts";
import useRealTimeAAA from "../../deal-builder/hooks/useRealTimeAAA.ts";
import useDealBuilderAPI from "../../deal-builder/hooks/useDealBuilderAPI.ts";
import moment from "moment";
import { ExpandedRatingOptions, RatingAssumption, SaleMethod, SecurityType, TaxDesignation, USState } from "../../deal-builder/services/utils/primary-constants.ts";
import { Bond } from "../../deal-builder/pages/debt-service-page.tsx";
import { ProductionTable } from "../../deal-builder/services/utils/production.utils.ts";
import usePricingManagementStore from "../../stores/pricing-management.store.ts";
import useDealBuilderSimilarDealsStore from "../../stores/deal-builder-similar-deals.store.ts";

const BACKEND_FETCH_ERROR_MSG = "We couldn't connect to our backend.";

export type DealBuilderSavedFormat = {
  // Amount settings
  par_amount: number;
  denomination: number;

  // Issuance settings
  borrower: string | undefined;
  issuance_title: string | undefined;

  // Production settings
  market_conditions_date: string;
  state_of_issuance: USState;
  security_type: SecurityType;
  rating_assumption: RatingAssumption;
  tax_designation: TaxDesignation;
  sale_method: SaleMethod;
  state_enhancement: ExpandedRatingOptions | undefined;
  bond_insurance: boolean;

  // Debt service settings
  delivery_date: string;
  first_coupon_date: string;
  call_date: string | undefined;

  // Sources and uses
  sources_and_uses_categories: string;
  capitalized_interest_settings: string | number;

  // Bonds and production
  bonds: string;
  production: string;

  // Misc
  cushion: number;

  // Pricing management data
  deal_id: string;
  deal_status: DealStatus;
  saved_date: string;
  bid_date: string;
  bid_rate: number;
  other_bids: [string, number][];
  comment: string;
  advisor: string;
  team_id?: string;
  team_name?: string;

  // Cached stats
  tic_expenses: number | undefined;
  tic: number | undefined;
  variance: number | undefined;
  variance_expenses: number | undefined;
  average_life: number;

  // Similar deals
  starred_similar_deals: string[] | undefined;

  // User information
  user_id: string;
};

export interface SpreadPrediction {
  yieldCurve: number[];
  spreads: number[];
  coupons: number[];
}

export default function useDatabaseAPI() {
  const { getAccessTokenSilently } = useAuth0();
  const { userInfo } = useContext(UserInfoContext);
  const { setLoading } = usePricingManagementStore();
  const { setSavedDeals: setNewSavedDeals } = usePricingManagementStore();

  const { getAlignedRealTimeAAA } = useRealTimeAAA();
  const { getAlignedAAA } = useEODAAA();
  const { getAlignedUSTreasury } = useUsTreasury();
  const { fetchCouponsAndSpreads } = useDealBuilderAPI();

  function toSavedFormat(builder: DealBuilder): DealBuilderSavedFormat {
    const variances = builder.calculateVariance();
    const starredSimilarDealCusips = useDealBuilderSimilarDealsStore.getState().starredSimilarDealCusips;

    return {
      par_amount: builder.getTotalPrincipal(),
      denomination: builder.denomination,
      borrower: builder.borrower,
      issuance_title: builder.issuanceTitle,
      market_conditions_date: builder.marketConditionsDate.toISOString(),
      state_of_issuance: builder.stateOfIssuance,
      security_type: builder.securityType,
      rating_assumption: builder.ratingAssumption,
      tax_designation: builder.taxDesignation,
      sale_method: builder.saleMethod,
      state_enhancement: builder.stateEnhancement,
      bond_insurance: builder.bondInsurance,
      delivery_date: builder.deliveryDate.toISOString(),
      first_coupon_date: builder.firstCouponDate.toISOString(),
      call_date: builder.callDate ? builder.callDate.toISOString() : undefined,
      sources_and_uses_categories: JSON.stringify(builder.sourcesAndUses),
      capitalized_interest_settings: typeof builder.capitalizedInterest === "number" ? 
        builder.capitalizedInterest :
        builder.capitalizedInterest.toISOString(),
      bonds: JSON.stringify(builder.bonds),
      production: JSON.stringify(builder.production.map(row => ({
        ...row,
        bond: builder.bonds.indexOf(row.bond)
      }))),
      cushion: builder.cushion,
      deal_id: builder.dealId,
      bid_date: builder.bidDate.toISOString(),
      deal_status: builder.dealStatus,
      saved_date: builder.savedDate.toISOString(),
      bid_rate: builder.bidRate,
      other_bids: builder.otherBids,
      comment: builder.comment,
      advisor: builder.advisor,
      
      tic_expenses: builder.adjustedTic,
      tic: builder.tic,
      average_life: builder.getAverageLife(),
      variance: variances[0],
      variance_expenses: variances[1],

      starred_similar_deals: starredSimilarDealCusips.length > 0 ? starredSimilarDealCusips : undefined,

      user_id: (userInfo as any).id,
    }
  }

  async function fromSavedFormat(builder: DealBuilderSavedFormat) {
    const bonds: Bond[] = JSON.parse(builder.bonds);
    const production: ProductionTable = (JSON.parse(builder.production) as any[])
      .map(row => ({
        ...row,
        bond: bonds[row.bond]
      }));

    const result = new DealBuilder({
      bondInsurance: builder.bond_insurance,
      borrower: builder.borrower || "",
      callDate: typeof builder.call_date === "string" ? moment(builder.call_date) : undefined,
      capitalizedInterest: typeof builder.capitalized_interest_settings === "string" ? 
        moment(builder.capitalized_interest_settings) : 
        builder.capitalized_interest_settings,
      deliveryDate: moment(builder.delivery_date),
      denomination: builder.denomination,
      firstCouponDate: moment(builder.first_coupon_date),
      bonds: bonds,
      issuanceTitle: builder.issuance_title || "",
      marketConditionsDate: moment(builder.market_conditions_date),
      ratingAssumption: builder.rating_assumption,
      saleMethod: builder.sale_method,
      securityType: builder.security_type,
      stateEnhancement: builder.state_enhancement,
      stateOfIssuance: builder.state_of_issuance,
      taxDesignation: builder.tax_designation,
      totalTargetParAmount: builder.par_amount,
      sourcesAndUses: JSON.parse(builder.sources_and_uses_categories),
      cushion: builder.cushion,
      fetchCouponsAndSpreads: fetchCouponsAndSpreads,
      getAlignedAAA: getAlignedAAA,
      getAlignedRealTimeAAA: getAlignedRealTimeAAA,
      getAlignedUSTreasury: getAlignedUSTreasury,
      saveDeal: saveDeal,
      updatePrincipals: false
    });

    result.isSaved = true;
    result.dealId = builder.deal_id;
    result.dealStatus = builder.deal_status;
    result.savedDate = moment(builder.saved_date);
    result.bidDate = moment(builder.bid_date);
    result.bidRate = builder.bid_rate;
    result.otherBids = builder.other_bids;
    result.comment = builder.comment;
    result.advisor = builder.advisor;
    
    result.production = production;
    result.updateDebtService(false);
    await result.updateProduction();

    return result;
  }

  async function saveDeal(builder: DealBuilder): Promise<Result<undefined>> {
    return loadingWrapper(async () => {
      if (!userInfo) return { valid: false, error: "Couldn't find your user information." }
      const token = await getAccessTokenSilently();
      const response = await PrimaryDatabaseAPI.upsertDeal(token, toSavedFormat(builder));
      if ("error" in response) return { valid: false, error: BACKEND_FETCH_ERROR_MSG };
      return { valid: true, body: undefined };
    });
  }

  async function loadDeals(): Promise<Result<DealBuilderSavedFormat[]>> {
    return loadingWrapper(async () => {
      const token = await getAccessTokenSilently();
      const response = await PrimaryDatabaseAPI.getDeals(token);
      if ("error" in response) return { valid: false, error: BACKEND_FETCH_ERROR_MSG };
      setNewSavedDeals(response.body);
      return { valid: true, body: response.body };
    });
  }

  async function deleteDeal(dealId: string): Promise<Result<undefined>> {
    return loadingWrapper(async () => {
      const token = await getAccessTokenSilently();
      return await PrimaryDatabaseAPI.deleteDeal(token, dealId);
    });
  }

  async function upsertDeal(deal: DealBuilderSavedFormat): Promise<Result<undefined>> {
    return loadingWrapper(async () => {
      if (!userInfo) return { valid: false, error: "Couldn't find your user information." }

      const token = await getAccessTokenSilently();
      const starredSimilarDealCusips = useDealBuilderSimilarDealsStore.getState().starredSimilarDealCusips;
      const response = await PrimaryDatabaseAPI.upsertDeal(token, {
        ...deal,
        // @ts-ignore
        user_id: userInfo.id,
        starred_similar_deals: starredSimilarDealCusips.length > 0 ? starredSimilarDealCusips : deal.starred_similar_deals,
      });
      if ("error" in response) return { valid: false, error: BACKEND_FETCH_ERROR_MSG };
      return { valid: true, body: undefined };
    });
  }

  async function loadingWrapper<T>(method: () => Promise<T>): Promise<T> {
    setLoading(true);
    try {
      return await method();
    } finally {
      setLoading(false);
    }
  }

  return { saveDeal, fromSavedFormat, toSavedFormat, loadDeals, deleteDeal, upsertDeal };
}
